import {FC, ReactNode, useEffect, useMemo, useState} from 'react'
import {Button, Form} from 'react-bootstrap'
import {ContractSuggestedItem} from '../../../../app/modules/contracts/components/contracts-list/core/_models'

type Props = {
  items: ContractSuggestedItem[]
  isCreating: boolean
  onConfirm: (selected: ContractSuggestedItem[]) => void
  onCancel: () => void
}

/** Metronic form-check-sm ≈ 1.55rem; sem label o Form.Check ignora o sm e usa ~1.75rem */
const CHECKBOX_PX = '1.3rem'

const checkInputStyle = {
  width: CHECKBOX_PX,
  height: CHECKBOX_PX,
  minWidth: CHECKBOX_PX,
  marginTop: 0,
} as const

const titleInputClass =
  'form-control form-control-sm form-control-solid form-control-flush w-100 text-gray-800 py-1 px-0 shadow-none'

type RushmindCheckboxProps = {
  id: string
  checked: boolean
  disabled?: boolean
  label?: ReactNode
  ariaLabel?: string
  onChange: (checked: boolean) => void
}

const RushmindCheckbox: FC<RushmindCheckboxProps> = ({
  id,
  checked,
  disabled,
  label,
  ariaLabel,
  onChange,
}) => (
  <div className='form-check form-check-custom form-check-solid mb-0 d-flex align-items-center flex-shrink-0 cursor-pointer'>
    <input
      type='checkbox'
      id={id}
      className='form-check-input cursor-pointer'
      style={checkInputStyle}
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      aria-label={typeof label === 'string' ? label : ariaLabel}
    />
    {label != null && (
      <label className='form-check-label text-gray-700 fs-7 mb-0 ms-2 cursor-pointer' htmlFor={id}>
        {label}
      </label>
    )}
  </div>
)

const ContractSuggestedItemsPreview: FC<Props> = ({items, isCreating, onConfirm, onCancel}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(items.map((i) => i.id)))
  const [titles, setTitles] = useState<Record<string, string>>(() =>
    Object.fromEntries(items.map((i) => [i.id, i.title]))
  )

  useEffect(() => {
    setTitles(Object.fromEntries(items.map((i) => [i.id, i.title])))
    setSelectedIds(new Set(items.map((i) => i.id)))
  }, [items])

  const resolvedItems = useMemo((): ContractSuggestedItem[] => {
    return items
      .filter((item) => selectedIds.has(item.id))
      .map((item) => ({
        ...item,
        title: (titles[item.id] ?? item.title).trim(),
        is_task: true,
        is_phase: false,
      }))
  }, [items, selectedIds, titles])

  const selectedCount = selectedIds.size

  const toggleIncluded = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const toggleAllIncluded = (checked: boolean) => {
    setSelectedIds(checked ? new Set(items.map((i) => i.id)) : new Set())
  }

  const handleConfirm = () => {
    onConfirm(resolvedItems)
  }

  const allIncluded = items.length > 0 && selectedIds.size === items.length
  const canConfirm = resolvedItems.length > 0

  const confirmLabel = () => {
    if (isCreating) return 'Criando...'
    const n = resolvedItems.length
    if (n === 0) return 'Confirmar'
    return `Criar ${n} tarefa${n === 1 ? '' : 's'}`
  }

  return (
    <div className='w-100'>
      <h6 className='fw-semibold text-gray-900 mb-1'>Tarefas sugeridas</h6>
      <p className='text-gray-600 fs-7 mb-3'>
        Revise e edite os títulos. Desmarque o que não quiser criar no projeto.
      </p>

      <div className='d-flex align-items-center justify-content-between gap-2 mb-2 pb-2 border-bottom border-gray-200'>
        <RushmindCheckbox
          id='contract-ai-items-select-all'
          label='Incluir todas'
          checked={allIncluded}
          onChange={toggleAllIncluded}
        />
        <span className='text-gray-500 fs-8 flex-shrink-0'>
          {selectedCount}/{items.length}
        </span>
      </div>

      <ul
        className='list-unstyled mb-4 w-100'
        style={{maxHeight: 'min(380px, 55vh)', overflowY: 'auto'}}
      >
        {items.map((item) => {
          const included = selectedIds.has(item.id)

          return (
            <li
              key={item.id}
              className={`d-flex align-items-center gap-2 py-1 ${
                included ? '' : 'opacity-40'
              }`}
            >
              <RushmindCheckbox
                id={`contract-ai-item-${item.id}`}
                checked={included}
                disabled={isCreating}
                ariaLabel={`Incluir tarefa ${item.title}`}
                onChange={() => toggleIncluded(item.id)}
              />
              <div className='flex-grow-1 min-w-0 border-bottom border-gray-200'>
                <Form.Control
                  type='text'
                  maxLength={255}
                  value={titles[item.id] ?? item.title}
                  disabled={isCreating || !included}
                  className={titleInputClass}
                  onChange={(e) =>
                    setTitles((prev) => ({...prev, [item.id]: e.target.value}))
                  }
                />
              </div>
            </li>
          )
        })}
      </ul>

      <div className='d-flex flex-wrap justify-content-end gap-2 pt-2 border-top border-gray-200'>
        <Button variant='light' size='sm' onClick={onCancel} disabled={isCreating}>
          Concluir sem criar
        </Button>
        <Button
          variant='primary'
          size='sm'
          onClick={handleConfirm}
          disabled={isCreating || !canConfirm}
        >
          {confirmLabel()}
        </Button>
      </div>
    </div>
  )
}

export {ContractSuggestedItemsPreview}
